import {
  HearingStageType,
  IHearingStage,
  MeetNotSummarizedReason,
} from 'Interfaces/MedicalCommittees';

export interface ICommitteeStep {
  key: HearingStageType;
  title: string;
  description: string;
  futureDescription?: string;
  externalLink?: string;
}

export const COMMITTEE_STEPS: ICommitteeStep[] = [
  {
    key: HearingStageType.PreMeeting,
    title: 'לפני דיון',
    description:
      'בשלב הדיון צוות הוועדה יתכנס כדי לנסות להבין כיצד מצבך הרפואי משפיע על ההתנהלות שלך',
    externalLink: 'https://shikum.mod.gov.il/recognition/committee',
  },
  {
    key: HearingStageType.FollowUpMeeting,
    title: 'דיון המשך',
    description: 'תיאור לשלב דיון המשך',
    externalLink: 'https://shikum.mod.gov.il/recognition/committee',
  },
  {
    key: HearingStageType.AwaitingProtocol,
    title: 'אחרי דיון, המתנה לפרוטוקול',
    description:
      'הדיון הסתיים, והפרוטוקול עבר לאישור יו"ר הוועדות. בקרוב נעלה אותו לכאן והוא יהיה זמין עבורך.',
    externalLink: 'https://shikum.mod.gov.il/recognition/committee',
  },
  {
    key: HearingStageType.ProtocolReceived,
    title: 'קבלת פרוטוקול',
    description: 'הפרוטוקול זמין עבורך בראש העמוד ובאזור המסמכים שלך',
    futureDescription: 'בשלב זה הפרוטוקול יהיה זמין עבורך באזור האישי',
  },
  {
    key: HearingStageType.AwaitingDecision,
    title: 'המתנה להחלטת קצין התגמולים',
    description:
      'החלטת הוועדה עברה לקצין התגמולים וממתינה לאישור, כדי לקבוע את אחוזי הנכות שלך.',
    futureDescription:
      'בשלב זה המלצת צוות הוועדה תעבור לקצין התגמולים כדי לקבוע את אחוזי הנכות שלך.',
    externalLink: 'https://shikum.mod.gov.il/recognition/committee',
  },
];

const getFollowUpMeetingDescription = (
  reason?: MeetNotSummarizedReason
): string => {
  switch (reason) {
    case MeetNotSummarizedReason.CommitteeRequestedPresence:
      return 'נקבע דיון נוסף כדי שצוות הוועדה יוכל להעריך את מצבך בצורה הטובה ביותר. נדרשת נוכחות בדיון';
    case MeetNotSummarizedReason.DifferentPanel:
      return 'לבקשת יו"ר הוועדות נקבע דיון נוסף. לא נדרשת נוכחות שלך, אבל אפשר להגיע לדיון';
    case MeetNotSummarizedReason.AdditionalDoctor:
      return 'נקבע דיון נוסף בנוכחות גורם רפואי שלא היה בדיון הקודם, כדי להעריך את מצבך בצורה מדויקת יותר';
    case MeetNotSummarizedReason.MissingMedicalDocuments:
      return 'נקבע דיון נוסף בעקבות חוסר במסמכים רפואיים. את ההשלמות הנדרשות אפשר לראות בראש העמוד';
    case MeetNotSummarizedReason.ReDiscussion:
      return 'נקבע דיון נוסף לבחינה מחודשת של המצב';
    default:
      return '';
  }
};

const parseDateToValid = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

export const getCommitteeSteps = (
  stages: IHearingStage[],
  currentStageId: HearingStageType
) => {
  const stagesByType = new Map<number, IHearingStage>();

  stages.forEach((stage) => {
    stagesByType.set(stage.stageType.id, stage);
  });

  const filteredSteps = COMMITTEE_STEPS.filter((step) => {
    return stagesByType.has(step.key);
  });

  const currentStepIndex = filteredSteps.findIndex(
    (s) => s.key === currentStageId
  );

  return filteredSteps.map((step, index) => {
    const stage = stagesByType.get(step.key);
    let title = step.title;
    let description = step.description;
    const externalLink = step.externalLink;

    const isCurrentStep = step.key === currentStageId;
    const isFutureStep = index > currentStepIndex;

    if (isFutureStep && step.futureDescription) {
      description = step.futureDescription;
    }

    if (step.key === HearingStageType.FollowUpMeeting && stage) {
      title = 'דיון נוסף בוועדה';
      description = getFollowUpMeetingDescription(
        stage.prevMeetNotSummarizedReason?.id
      );
    }

    return {
      title,
      description,
      externalLink,
      date: isFutureStep ? undefined : parseDateToValid(stage?.date),
      isActive: isCurrentStep,
    };
  });
};
